import { useEffect, useMemo } from "react";
import { Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { PLAYER_REPORT_DATA_STREAMS, REPORT_TYPES } from "@components/Report/constants";
import { buildFlagTypeOptions } from "@components/StatusTrackerV2/mappers/flags.mapper";
import { getUserDefinedFlags } from "@store/actions/userDefinedFlags.actions";
import { getMetricInfoByIdentifier } from "@utils/chart_utils";
import { isStaffEmail } from "@utils/user_utils";

const { Text } = Typography;

const AGGREGATE_COLUMN_LABELS: Record<string, string> = {
    player_value: 'Player Value',
    position_value: 'Position Value',
    team_value: 'Team Value',
    player_zscore: 'Z-Score',
    player_tscore: 'T-Score',
    player_tscore_desc: 'T-Score (Desc)',
    player_percentile: 'Percentile',
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <>
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{label}</Text>
        <Text style={{ fontSize: 12 }}>{children}</Text>
    </>
);

interface PresetConfigDisplayProps {
    preset: any;
}

export const PresetConfigDisplay = ({ preset }: PresetConfigDisplayProps) => {
    const { allPlayers, playerGroups, all_metric_types: allMetricTypes, info } = useSelector(({ team }: any) => team);
    const udfFlags = useSelector(({ userDefinedFlags }: any) => userDefinedFlags?.flags || []);
    const dispatch = useDispatch();
    const { user } = useAuth0();
    // The backend refuses the flags (403) when the team does not have the feature and the user is not staff.
    const udfEnabled = (info?.user_defined_flags_enabled ?? false) || isStaffEmail(user?.email);

    const config = preset?.config;
    const reportType = REPORT_TYPES.find(r => r.key === config?.report_type);
    const reportTypeLabel = reportType?.label;
    const flagFilter: string[] = config?.flag_filter || [];

    const needsFlagNames = flagFilter.some((value) => String(value).startsWith('udf:'));
    useEffect(() => {
        if (needsFlagNames && udfEnabled && udfFlags.length === 0) dispatch(getUserDefinedFlags() as any);
    }, [needsFlagNames, udfEnabled, udfFlags.length, dispatch]);

    const flagLabelByValue = useMemo(() => {
        const labels = new Map<string, string>();
        buildFlagTypeOptions(udfFlags).forEach((option: any) => labels.set(option.value, option.label));
        return labels;
    }, [udfFlags]);

    if (!preset || !config) return null;

    const metricRows = Object.entries(config)
        .filter(([key]) => key.endsWith('-metrics') && Array.isArray(config[key]) && config[key].length > 0)
        .map(([key, metrics]) => {
            const streamName = key.replace('-metrics', '');
            const streamLabel = PLAYER_REPORT_DATA_STREAMS.find(s => s.key === streamName)?.label
                || streamName.split(/[-_]/).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            const names = (metrics as any[]).map(m => {
                const id = typeof m === 'string' ? m : m?.metric;
                return id ? getMetricInfoByIdentifier(id, allMetricTypes)?.[0] || id : null;
            }).filter(Boolean).join(', ');
            return { key, label: `${streamLabel} Metrics`, value: names };
        });

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3, alignItems: 'baseline' }}>
            {reportTypeLabel && <Row label="Report Type">{reportTypeLabel}</Row>}
            {config.position_filter?.length > 0 && <Row label="Positions">{config.position_filter.join(', ')}</Row>}
            {config.player_group_filter?.length > 0 && (
                <Row label="Player Groups">
                    {config.player_group_filter.map((id: any) => playerGroups?.find((g: any) => g.id === id)?.name || id).join(', ')}
                </Row>
            )}
            {config.player_filter && !Array.isArray(config.player_filter) && (
                <Row label="Player">
                    {allPlayers?.find((p: any) => p.player_id === config.player_filter)?.name || config.player_filter}
                </Row>
            )}
            {Array.isArray(config.player_filter) && config.player_filter.length > 0 && (
                <Row label="Players">
                    {config.player_filter
                        .map((id: any) => id === 'full-team' ? 'Full Team' : allPlayers?.find((p: any) => p.player_id === id)?.name || id)
                        .join(', ')}
                </Row>
            )}
            {flagFilter.length > 0 && (
                <Row label="Flags">
                    {flagFilter.map((value: string) => flagLabelByValue.get(value) || value).join(', ')}
                </Row>
            )}
            {flagFilter.length === 0 && reportType?.requirements?.includes('flag_filter') && (
                <Row label="Flags">All flags</Row>
            )}
            {config.group_by_position !== undefined && (
                <Row label="Group by Position">{config.group_by_position ? 'Yes' : 'No'}</Row>
            )}
            {config.aggregate_option && (
                <Row label="Aggregate">{config.aggregate_option === 'avg' ? 'Average' : 'Best'}</Row>
            )}
            {config.aggregate_columns?.length > 0 && (
                <Row label="Aggregate Columns">
                    {config.aggregate_columns.map((col: string) => AGGREGATE_COLUMN_LABELS[col] || col).join(', ')}
                </Row>
            )}
            {config.weekly_streams?.length > 0 && <Row label="Data Streams">{config.weekly_streams.join(', ')}</Row>}
            {config.player_report_data_streams?.length > 0 && (
                <Row label="Report Sections">
                    {config.player_report_data_streams
                        .map((key: string) => PLAYER_REPORT_DATA_STREAMS.find(s => s.key === key)?.label || key)
                        .join(', ')}
                </Row>
            )}
            {metricRows.map(({ key, label, value }) => (
                <Row key={key} label={label}>{value}</Row>
            ))}
        </div>
    );
};

export default PresetConfigDisplay;
